import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { Module } from 'src/app/models/module';
import * as moduleActions from './module.actions';
import * as calculatedVariableActions from './../calculated-variable/calculated-variable.actions';
import { CalculatedVariable } from '../../../models/calculated-variable';
import { sortByDate } from '../../studies/helpers/date-helper';

export interface ModuleState extends EntityState<Module> {
  loadingModules: boolean;
  modulesLoaded: boolean;
  loadingModule: boolean;
  moduleLoaded: boolean;
  selectedModule: Module;
  calculatedVariablesForModule: CalculatedVariable[];
  modulesInSelectedIntervention: Module[];
}

export function sortByCreateDate(module1: Module, module2: Module): number {
  return sortByDate(module1?.createdAt, module2?.createdAt);
}

export const moduleAdapter: EntityAdapter<Module> = createEntityAdapter<Module>({
  sortComparer: sortByCreateDate,
});

export const initialModuleState: ModuleState = moduleAdapter.getInitialState({
  loadingModules: false,
  modulesLoaded: false,
  loadingModule: false,
  moduleLoaded: false,
  selectedModule: null,
  calculatedVariablesForModule: null,
  modulesInSelectedIntervention: [],
});

export const reducer = createReducer(
  initialModuleState,
  on(moduleActions.getModulesSuccess, (state, { modules }) => {
    return moduleAdapter.setAll(modules, {
      ...state,
      loadingModules: false,
      modulesLoaded: true,
    });
  }),
  on(moduleActions.getModulesInSelectedIntervention, (state) => {
    return {
      ...state,
      loadingModules: true,
      modulesLoaded: false,
    };
  }),
  on(moduleActions.getModulesInSelectedInterventionSuccess, (state, { modules }) => {
    return {
      ...state,
      loadingModules: false,
      modulesLoaded: true,
      modulesInSelectedIntervention: modules,
    };
  }),
  on(moduleActions.getModulesInSelectedInterventionError, (state) => {
    return {
      ...state,
      loadingModules: false,
      modulesLoaded: false,
    };
  }),
  on(moduleActions.setSelectedModule, (state, { selectedModule }) => {
    return {
      ...state,
      selectedModule,
    };
  }),
  on(moduleActions.createModuleSuccess, (state, { newModule }) => {
    return moduleAdapter.addOne(newModule, {
      ...state,
      selectedModule: newModule,
    });
  }),
  on(moduleActions.getModule, (state) => {
    return {
      ...state,
      loadingModule: true,
      moduleLoaded: false,
    };
  }),
  on(moduleActions.getModuleSuccess, (state, { selectedModule }) => {
    return moduleAdapter.updateOne(
      { id: selectedModule.id, changes: selectedModule },
      {
        ...state,
        loadingModule: false,
        moduleLoaded: true,
        selectedModule,
      }
    );
  }),
  on(moduleActions.updateModuleSuccess, (state, { updatedModule }) => {
    return moduleAdapter.updateOne(
      { id: updatedModule.id, changes: updatedModule },
      {
        ...state,
        selectedModule: updatedModule,
      }
    );
  }),
  on(moduleActions.duplicateModuleSuccess, (state, { duplicatedModule }) => {
    return moduleAdapter.addOne(duplicatedModule, {
      ...state,
      duplicatedModule,
    });
  }),
  on(calculatedVariableActions.getCalculatedVariablesForModuleSuccess, (state, { calculatedVariablesForModule }) => {
    return {
      ...state,
      calculatedVariablesForModule,
    };
  }),
  on(moduleActions.deleteModuleSuccess, (state, { deletedModule }) => {
    return moduleAdapter.removeOne(deletedModule.id, {
      ...state,
      selectedModule: null,
    });
  })
);

export function moduleReducer(state: ModuleState | undefined, action: Action) {
  return reducer(state, action);
}

export const { selectIds, selectEntities, selectAll, selectTotal } = moduleAdapter.getSelectors();
